import React, { useEffect, useState } from "react";
import { Box, Grid, TextField, Typography } from "@mui/material";
import InputMask from "react-input-mask";
import * as Yup from "yup";
import { Formik } from "formik";
import { LoadingButton } from "@mui/lab";
import SnackbarProvider from "app/components/SnackbarProvider";
import { ArrowRightAltOutlined } from "@mui/icons-material";
import { getAddressByZipCode } from "app/services/profileServices/ZipCodeService";
import {
  getContactInfo,
  updateContact,
} from "app/services/profileServices/Profile";
import { MatxLoading } from "app/components";
import { notify } from "app/utils/notifyUtils";

interface FormValues {
  zipcode: string;
  neighborhood: string;
  city: string;
  complement: string;
  uf: string;
  address: string;
  number: string;
  telephone: string;
  cellphone: string;
}

const validationSchema = Yup.object().shape({
  zipcode: Yup.string()
    .min(9, "O CEP deve estar completo")
    .required("CEP é um campo obrigatório"),
  neighborhood: Yup.string().required("Bairro é um campo obrigatório"),
  city: Yup.string().required("Cidade é um campo obrigatório"),
  uf: Yup.string()
    .min(2, "UF é um campo obrigatório")
    .required("UF é um campo obrigatório"),
  address: Yup.string().required("Endereço é um campo obrigatório"),
  number: Yup.string().required("Número é um campo obrigatório"),
  // telephone: Yup.string()
  //   .min(13, "O telefone deve estar completo")
  //   .required("Telefone é um campo obrigatório"),
  cellphone: Yup.string()
    .min(14, "O celular deve estar completo")
    .required("Celular é um campo obrigatório"),
});

const FormAddress = () => {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const snackbarProvider = SnackbarProvider();

  const [initialValues, setInitialValues] = useState<FormValues>({
    zipcode: "",
    neighborhood: "",
    city: "",
    complement: "",
    uf: "",
    address: "",
    number: "",
    telephone: "",
    cellphone: "",
  });

  const handleFormSubmit = async (values: FormValues) => {
    setFormLoading(true);
    try {
      await updateContact(values);
      notify("success", "Dados enviados com sucesso!");
      window.location.reload();
    } catch (error) {
      notify("error", "Erro ao enviar dados. Tente novamente.");
    } finally {
      setFormLoading(false);
    }
  };

  const blurCep = async (cep: string) => {
    try {
      if (cep !== "_____-___") return await getAddressByZipCode(cep);
    } catch (error) {
      console.log(error);
      notify("info", "O CEP informado não foi localizado");
      return [];
    }
  };

  useEffect(() => {
    setLoading(true);
    const loadUserContactInfo = async () => {
      const data: any = await getContactInfo();
      const userData = data.data.response;
      if (userData) {
        setInitialValues({
          zipcode: userData.zip_code,
          neighborhood: userData.neighborhood,
          city: userData.city,
          complement: userData.complement,
          uf: userData.uf,
          address: userData.address,
          number: userData.number,
          telephone: userData.telephone,
          cellphone: userData.cellphone,
        });
      }
      setLoading(false);
    };

    loadUserContactInfo();
  }, []);

  if (loading) {
    return (
      <div style={{ height: "10vh" }}>
        <MatxLoading />
      </div>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 3,
        width: "100%",
        mx: "auto",
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          marginBottom: 1,
          fontWeight: 600,
          color: "#1a237e",
          letterSpacing: "-0.5px",
        }}
      >
        Informações de Contato
      </Typography>

      <Typography variant="body1" sx={{ marginBottom: 3, color: "#000" }}>
        Preencha os campos abaixo para cadastrar seus dados de contato
      </Typography>

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => {
          const handleCepBlur = async (e: any) => {
            handleBlur(e);
            const addressData: any = await blurCep(values.zipcode);
            if (addressData) {
              setFieldValue("address", addressData.data.logradouro || "");
              setFieldValue("neighborhood", addressData.data.bairro || "");
              setFieldValue("city", addressData.data.localidade || "");
              setFieldValue("uf", addressData.data.uf || "");
            }
          };

          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3} marginBottom={3}>
                <Grid item xs={12} sm={4}>
                  <InputMask
                    mask="99999-999"
                    value={values.zipcode}
                    onChange={handleChange}
                    onBlur={handleCepBlur}
                  >
                    {() => (
                      <div>
                        <TextField
                          autoComplete="off"
                          fullWidth
                          size="medium"
                          type="text"
                          name="zipcode"
                          label="CEP"
                          variant="outlined"
                          onBlur={handleCepBlur}
                          value={values.zipcode}
                          onChange={handleChange}
                          helperText={touched.zipcode && errors.zipcode}
                          error={Boolean(errors.zipcode && touched.zipcode)}
                        />
                      </div>
                    )}
                  </InputMask>
                </Grid>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    size="medium"
                    type="text"
                    name="address"
                    label="Endereço"
                    variant="outlined"
                    onBlur={handleBlur}
                    value={values.address}
                    onChange={handleChange}
                    helperText={touched.address && errors.address}
                    error={Boolean(errors.address && touched.address)}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    size="medium"
                    type="text"
                    name="number"
                    label="Número"
                    variant="outlined"
                    onBlur={handleBlur}
                    value={values.number}
                    onChange={handleChange}
                    helperText={touched.number && errors.number}
                    error={Boolean(errors.number && touched.number)}
                  />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    size="medium"
                    type="text"
                    name="neighborhood"
                    label="Bairro"
                    variant="outlined"
                    onBlur={handleBlur}
                    value={values.neighborhood}
                    onChange={handleChange}
                    helperText={touched.neighborhood && errors.neighborhood}
                    error={Boolean(errors.neighborhood && touched.neighborhood)}
                  />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    size="medium"
                    type="text"
                    name="complement"
                    label="Complemento"
                    variant="outlined"
                    onBlur={handleBlur}
                    value={values.complement}
                    onChange={handleChange}
                    helperText={touched.complement && errors.complement}
                    error={Boolean(errors.complement && touched.complement)}
                  />
                </Grid>

                <Grid item xs={12} sm={5}>
                  <TextField
                    autoComplete="off"
                    fullWidth
                    size="medium"
                    type="text"
                    name="city"
                    label="Cidade"
                    variant="outlined"
                    onBlur={handleBlur}
                    value={values.city}
                    onChange={handleChange}
                    helperText={touched.city && errors.city}
                    error={Boolean(errors.city && touched.city)}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <InputMask
                    mask="aa"
                    value={values.uf}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {() => (
                      <TextField
                        autoComplete="off"
                        fullWidth
                        size="medium"
                        type="text"
                        name="uf"
                        label="UF"
                        variant="outlined"
                        onBlur={handleBlur}
                        value={values.uf}
                        onChange={handleChange}
                        helperText={touched.uf && errors.uf}
                        error={Boolean(errors.uf && touched.uf)}
                      />
                    )}
                  </InputMask>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputMask
                    mask="(99) 9999-9999"
                    value={values.telephone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {() => (
                      <TextField
                        autoComplete="off"
                        fullWidth
                        size="medium"
                        type="text"
                        name="telephone"
                        label="Telefone"
                        variant="outlined"
                        onBlur={handleBlur}
                        value={values.telephone}
                        onChange={handleChange}
                        helperText={touched.telephone && errors.telephone}
                        error={Boolean(errors.telephone && touched.telephone)}
                      />
                    )}
                  </InputMask>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputMask
                    mask="(99)99999-9999"
                    value={values.cellphone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {() => (
                      <TextField
                        autoComplete="off"
                        fullWidth
                        size="medium"
                        type="text"
                        name="cellphone"
                        label="Celular"
                        variant="outlined"
                        onBlur={handleBlur}
                        value={values.cellphone}
                        onChange={handleChange}
                        helperText={touched.cellphone && errors.cellphone}
                        error={Boolean(errors.cellphone && touched.cellphone)}
                      />
                    )}
                  </InputMask>
                </Grid>

                <Grid item xs={6} sm={6}>
                  <LoadingButton
                    type="submit"
                    color="primary"
                    size="large"
                    loading={formLoading}
                    variant="contained"
                    endIcon={<ArrowRightAltOutlined fontSize="small" />}
                  >
                    Cadastrar
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
            //aq
          );
        }}
      </Formik>
    </Box>
  );
};

export default FormAddress;
